/**
 * PanelStackContainer
 *
 * Horizontal layout container for ALL panels:
 * Sidebar → Navigator → Content Panel(s) with resize sashes.
 *
 * Content panels use CSS flex-grow with their proportions as weights:
 * - Each panel gets `flex: <proportion> 1 0px` with `min-width: PANEL_MIN_WIDTH`
 * - Flex distributes available space proportionally — panels fill the viewport
 * - When panels hit min-width, overflow-x: auto kicks in naturally
 *
 * Sidebar and Navigator are NOT part of the proportional layout —
 * they have their own fixed/user-resizable widths managed by AppShell.
 * They just reduce the available width for content panels and scroll with everything else.
 *
 * The right sidebar stays OUTSIDE this container.
 */

import { useRef, useEffect } from 'react'
import { useAtomValue } from 'jotai'
import { motion } from 'motion/react'
import { cn } from '@/lib/utils'
import { panelStackAtom, focusedPanelIdAtom } from '@/atoms/panel-stack'
import { PanelSlot } from './PanelSlot'
import { PanelResizeSash } from './PanelResizeSash'
import {
  PANEL_GAP,
  PANEL_EDGE_INSET,
} from './panel-constants'

/** Spring transition matching AppShell's sidebar/navigator animation */
const PANEL_SPRING = { type: 'spring' as const, stiffness: 600, damping: 49 }

interface PanelStackContainerProps {
  sidebarSlot: React.ReactNode
  sidebarWidth: number
  navigatorSlot: React.ReactNode
  navigatorWidth: number
  isSidebarAndNavigatorHidden: boolean
  isRightSidebarVisible?: boolean
  isResizing?: boolean
  layoutMode?: 'mobile' | 'tablet' | 'desktop'
  mobileView?: 'list' | 'chat'
}

export function PanelStackContainer({
  sidebarSlot,
  sidebarWidth,
  navigatorSlot,
  navigatorWidth,
  isSidebarAndNavigatorHidden,
  isRightSidebarVisible,
  isResizing,
  layoutMode = 'desktop',
  mobileView = 'list',
}: PanelStackContainerProps) {
  const panelStack = useAtomValue(panelStackAtom)
  const focusedPanelId = useAtomValue(focusedPanelIdAtom)

  const contentPanels = panelStack

  const scrollRef = useRef<HTMLDivElement>(null)
  const prevCountRef = useRef(contentPanels.length)

  const isMobileLayout = layoutMode === 'mobile'
  const isTabletLayout = layoutMode === 'tablet'
  const hasSidebar = layoutMode !== 'mobile' && layoutMode !== 'tablet' && sidebarWidth > 0
  const hasNavigator = navigatorWidth > 0
  const isMultiPanel = contentPanels.length > 1
  const isLeftEdge = isMobileLayout || (!hasSidebar && !hasNavigator)

  // Auto-scroll to newly pushed content panel
  useEffect(() => {
    if (contentPanels.length > prevCountRef.current && scrollRef.current) {
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({
          left: scrollRef.current.scrollWidth,
          behavior: 'smooth',
        })
      })
    }
    prevCountRef.current = contentPanels.length
  }, [contentPanels.length])

  const transition = isResizing ? { duration: 0 } : PANEL_SPRING

  return (
    <div
      ref={scrollRef}
      className="flex-1 min-w-0 flex relative z-panel panel-scroll"
      style={{
        overflowX: isMobileLayout ? 'hidden' : 'auto',
        overflowY: 'hidden',
      }}
    >
      {/* Inner flex container — flex-grow: 1 fills viewport, content can overflow for scroll.
           Animated paddingLeft provides window-edge spacing when sidebar/navigator are hidden.
           Hidden slots use marginRight: -PANEL_GAP to cancel their trailing flex gap. */}
      <motion.div
        className="flex h-full"
        initial={false}
        animate={{ paddingLeft: !hasSidebar && !isMobileLayout ? PANEL_EDGE_INSET : 0 }}
        transition={transition}
        style={{ gap: isMobileLayout ? 0 : PANEL_GAP, flexGrow: 1, minWidth: 0 }}
      >
        {/* === SIDEBAR SLOT === */}
        <motion.div
          initial={false}
          animate={{
            width: hasSidebar ? sidebarWidth : 0,
            marginRight: hasSidebar ? 0 : -PANEL_GAP,
            opacity: hasSidebar ? 1 : 0,
          }}
          transition={transition}
          className="h-full relative shrink-0"
          style={{ overflowX: 'clip', overflowY: 'visible' }}
        >
          <div className="h-full" style={{ width: sidebarWidth }}>
            {sidebarSlot}
          </div>
        </motion.div>

        {/* === NAVIGATOR SLOT === */}
        {/* On mobile, keep both navigator and content mounted to preserve scroll/state;
            use CSS visibility + position to hide the inactive view. */}
        <motion.div
          initial={false}
          animate={isMobileLayout ? { opacity: 1 } : {
            width: hasNavigator ? navigatorWidth : 0,
            marginRight: hasNavigator ? 0 : -PANEL_GAP,
            opacity: hasNavigator ? 1 : 0,
          }}
          transition={isMobileLayout ? { duration: 0 } : transition}
          aria-hidden={isMobileLayout && mobileView !== 'list' ? true : undefined}
          className={cn(
            'h-full overflow-hidden relative shrink-0 z-[2]',
            'bg-background',
            // Flat design: hairline seam against the sidebar; window edge gets no border.
            hasSidebar && 'border-l border-border-strong',
            isMobileLayout && 'flex-1 !w-auto',
            isMobileLayout && mobileView !== 'list' && 'invisible absolute inset-0 pointer-events-none',
          )}
          style={isMobileLayout ? { width: '100%' } : undefined}
        >
          <div className={cn('h-full', isMobileLayout && 'min-w-0')} style={isMobileLayout ? { width: '100%' } : { width: navigatorWidth }}>
            {navigatorSlot}
          </div>
        </motion.div>

        {/* === CONTENT PANELS WITH SASHES === */}
        <div
          className={cn(
            isMobileLayout && 'flex-1 flex min-w-0',
            isMobileLayout && mobileView !== 'chat' && 'invisible absolute inset-0 pointer-events-none',
            !isMobileLayout && 'contents',
          )}
          aria-hidden={isMobileLayout && mobileView !== 'chat' ? true : undefined}
        >
          {contentPanels.length === 0 ? (
            <div className="flex-1 flex items-center justify-center" />
          ) : (
            contentPanels.map((entry, index) => (
              <PanelSlot
                key={entry.id}
                entry={entry}
                isOnly={contentPanels.length === 1}
                isFocusedPanel={isMultiPanel ? entry.id === focusedPanelId : true}
                isSidebarAndNavigatorHidden={isSidebarAndNavigatorHidden}
                isAtLeftEdge={index === 0 && isLeftEdge}
                isAtRightEdge={index === contentPanels.length - 1 && !isRightSidebarVisible}
                proportion={isMobileLayout ? 1 : entry.proportion}
                sash={index > 0 && !isMobileLayout && !isTabletLayout ? (
                  <PanelResizeSash
                    leftIndex={index - 1}
                    rightIndex={index}
                  />
                ) : undefined}
              />
            ))
          )}
        </div>
      </motion.div>
    </div>
  )
}
